import type { Method, Request, Response } from './broadcast-rpc.types';
import { isRequest, isResponse } from './broadcast-rpc.types';

/**
 * Tiny library for procedure calls over Broadcast Channel API, loosly based on JSON-RPC spec.
 *
 * BroadcastChannel creates a message passing environment (channel)
 * that allows communication between different documents (windows, tabs) of the same origin.
 * This library implements method calls over that channel - one document sends a request, others can respond.
 * It's best described as Broadcast RPC with some quirks:
 * - Unlike normal RPC where only one response is expected, the request goes to all documents and multiple can respond
 * Only the first response is captured and others are discarded.
 * As an extension, we can collect all responses and return multiple results to the caller.
 * - It's not really "Remote" since communication happens within the same browser, but it follows RPC semantics
 *
 * NOTE: A channel won't broadcast to itself. The BroadcastChannel API sends messages to other browsing contexts.
 */
const getId = () => crypto.randomUUID().split('-')[0]!;

// Dispatch table. Maps methods names to the function resolving the method.
const methods = new Map<string, Method>();

// Channel to send RPC requests and responses over.
const channel = new BroadcastChannel('upx-framework');

// Clean up channel during dev hot reload.
// Otherwise multiple channels will be created with multiple listeners.
if (import.meta.hot) {
  import.meta.hot.dispose(() => channel.close());
}

function dispatch(req: Request, ch: BroadcastChannel) {
  const method = methods.get(req.method);
  if (method) {
    const result = method();
    const res: Response = {
      id: req.id,
      result: result,
    };
    ch.postMessage(res);
  }
}

// Dispatch RPC requests
channel.onmessage = ({ data: req }) => {
  if (!isRequest(req)) {
    return;
  }
  dispatch(req, channel);
};

/**
 * Registers a method handler that can be called by other documents via broadcast channel.
 * When a method call is received over the channel, the registered handler will be invoked.
 *
 * @param name the name of the method to register
 * @param handler The function to call when this method is invoked
 *
 * @example
 * // NOTE: A channel won't broadcast to itself. Only handlers from other tabs will respond.
 * register('getUserData', () => ({ name: 'John' })); // tab A
 * const userData = await call('getUserData'); // tab B
 */
export function register(name: string, handler: (...args: unknown[]) => unknown) {
  methods.set(name, handler);
}

/**
 * Calls a method on other documents via broadcast channel and waits for response(s).
 * Broadcasts the method call to all documents in the same origin. Currently, only
 * the first response is returned, in the future can be configured to collect all responses.
 *
 * @param name The name of the method to call
 * @param options Timeout in milliseconds to wait for response
 * @returns Promise that resolves to the method ersult
 * @throws If the call timesout
 *
 * @example
 * // NOTE: A channel won't broadcast to itself. Only handlers from other tabs will respond.
 * register('getUserData', () => ({ name: 'John' })); // tab A
 * const userData = await call('getUserData'); // tab B
 */
export function call<T = unknown>(name: string, options = { timeout: 100 }): Promise<T> {
  const req: Request = {
    id: getId(),
    method: name,
  };
  channel.postMessage(req);

  const { promise, resolve, reject } = Promise.withResolvers<T>();

  const responseHandler = ({ data }: MessageEvent) => {
    if (!isResponse(data)) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const res = data as Response<T>;

    if (res.id === req.id) {
      resolve(res.result);
      channel.removeEventListener('message', responseHandler);
    }
  };

  setTimeout(() => {
    channel.removeEventListener('message', responseHandler);
    reject(new Error(`request timeout: ${req.method} ${req.id}`));
  }, options.timeout);

  channel.addEventListener('message', responseHandler);

  return promise;
}
